import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const accessStatusSchema = z.enum(['pending', 'active', 'expired', 'revoked', 'declined']);
const accessRowSchema = z.object({
  grant_id: z.string().uuid(),
  email_masked: z.string(),
  accountant_name: z.string(),
  status: accessStatusSchema,
  scope: z.literal('business_only'),
  tax_year: z.number().int(),
  expires_at: z.string(),
  last_opened_at: z.string().nullable(),
  updated_at: z.string(),
}).strict();

export const accountantAccessSchema = z.object({ access: z.array(accessRowSchema) }).strict();
const ownerInvitationSchema = z.object({
  grant_id: z.string().uuid(),
  status: z.literal('pending'),
  expires_at: z.string(),
}).strict();
const accessMutationSchema = z.object({
  grant_id: z.string().uuid(),
  status: accessStatusSchema,
  updated_at: z.string(),
}).strict();

export type AccountantAccess = z.infer<typeof accountantAccessSchema>;
export type AccessRow = z.infer<typeof accessRowSchema>;

export function useAccountantAccess(taxYear?: number) {
  return useQuery({
    queryKey: ['accountant-access', taxYear ?? null],
    queryFn: async () => {
      const response = taxYear === undefined
        ? await client.get('/api/accountant-access')
        : await client.get('/api/accountant-access', { params: { tax_year: taxYear } });
      return parseEnvelope(accountantAccessSchema, response.data).data;
    },
  });
}

export function useInviteAccountant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; tax_year: number }) => {
      const response = await client.post('/api/accountant-access/invitations', input);
      return parseEnvelope(ownerInvitationSchema, response.data).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accountant-access'] }),
  });
}

export function useMutateAccountantAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      grantId: string;
      action: 'resend' | 'cancel' | 'revoke';
      expectedUpdatedAt: string;
    }) => {
      const response = await client.post(
        `/api/accountant-access/${input.grantId}/${input.action}`,
        { expected_updated_at: input.expectedUpdatedAt },
      );
      return parseEnvelope(accessMutationSchema, response.data).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accountant-access'] }),
  });
}
